import { forwardRef, useMemo, useState } from 'react'
import { format, isSameDay, isValid, startOfDay } from 'date-fns'
import { ArrowUpDown, CalendarIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import { Label } from './label'
import { Icon } from '@/common/components/Icon'

type DateTimePickerProps = {
  label?: string
  value?: Date | null
  onChange: (value: Date | null) => void
  placeholder?: string
  disabled?: boolean
  minuteStep?: number
  className?: string
  hasError?: boolean
  mode?: 'date' | 'datetime' | 'time'
  labelClassName?: string
  align?: 'center' | 'start' | 'end'
  minDate?: Date | null
  maxDate?: Date | null
}

const pad2 = (value: number) => String(value).padStart(2, '0')

const getTimeParts = (value?: Date | null) => {
  if (!value || !isValid(value)) {
    return { hour: '12', minute: '00', period: 'AM' as 'AM' | 'PM' }
  }
  const hours24 = value.getHours()
  const period = hours24 >= 12 ? 'PM' : 'AM'
  const hour12 = hours24 % 12 || 12
  return { hour: pad2(hour12), minute: pad2(value.getMinutes()), period }
}

/** Calendar-day Date that keeps the same yyyy-MM-dd in local time and in ISO/UTC. */
const toCalendarDate = (date: Date) =>
  new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0),
  )

const clockMinutes = (date: Date) => date.getHours() * 60 + date.getMinutes()

const partsToMinutes = (hour: string, minute: string, period: 'AM' | 'PM') => {
  let hours = Number(hour) % 12
  if (period === 'PM') hours += 12
  return hours * 60 + Number(minute)
}

const buildDateWithTime = (
  date: Date,
  hour: string,
  minute: string,
  period: 'AM' | 'PM',
) => {
  let hours = Number(hour) % 12
  if (period === 'PM') hours += 12
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    Number(minute),
    0,
    0,
  )
}

export const DateTimePicker = forwardRef<
  HTMLButtonElement,
  DateTimePickerProps
>(
  (
    {
      value,
      onChange,
      placeholder = 'Pick a date',
      disabled = false,
      minuteStep = 5,
      className,
      label,
      hasError,
      mode = 'datetime',
      labelClassName,
      align = 'center',
      minDate,
      maxDate,
    }: DateTimePickerProps,
    ref,
  ) => {
    const [open, setOpen] = useState(false)
    const [internalValue, setInternalValue] = useState<Date | null>(
      value ?? null,
    )
    const resolvedValue = value !== undefined ? value : internalValue
    const safeValue =
      resolvedValue && isValid(resolvedValue) ? resolvedValue : null
    const timeParts = useMemo(() => getTimeParts(safeValue), [safeValue])
    const calendarDisabled = useMemo(() => {
      const matchers: Array<{ before: Date } | { after: Date }> = []
      if (minDate && isValid(minDate)) {
        matchers.push({ before: startOfDay(minDate) })
      }
      if (maxDate && isValid(maxDate)) {
        matchers.push({ after: startOfDay(maxDate) })
      }
      return matchers.length ? matchers : undefined
    }, [minDate, maxDate])

    const isDayAllowed = (date: Date) => {
      const day = startOfDay(date)
      if (minDate && isValid(minDate) && day < startOfDay(minDate)) {
        return false
      }
      if (maxDate && isValid(maxDate) && day > startOfDay(maxDate)) {
        return false
      }
      return true
    }

    const isClockAllowed = (
      hour: string,
      minute: string,
      period: 'AM' | 'PM',
      refDate: Date = safeValue ?? new Date(),
    ) => {
      const mins = partsToMinutes(hour, minute, period)
      const limitMin =
        !!minDate &&
        isValid(minDate) &&
        (mode === 'time' || isSameDay(refDate, minDate))
      const limitMax =
        !!maxDate &&
        isValid(maxDate) &&
        (mode === 'time' || isSameDay(refDate, maxDate))
      if (limitMin && minDate && mins < clockMinutes(minDate)) return false
      if (limitMax && maxDate && mins > clockMinutes(maxDate)) return false
      return true
    }

    const snapToMinuteStep = (date: Date) => {
      const next = new Date(date)
      const step = minuteStep > 0 ? minuteStep : 1
      const total = next.getHours() * 60 + next.getMinutes()
      const snapped = Math.round(total / step) * step
      const clamped = Math.min(Math.max(snapped, 0), 24 * 60 - step)
      next.setHours(Math.floor(clamped / 60), clamped % 60, 0, 0)
      return next
    }

    const isValueAllowed = (date: Date) => {
      if (mode === 'date') return isDayAllowed(date)
      const parts = getTimeParts(date)
      const clockOk = isClockAllowed(
        parts.hour,
        parts.minute,
        parts.period as 'AM' | 'PM',
        date,
      )
      if (mode === 'time') return clockOk
      return isDayAllowed(date) && clockOk
    }

    const nowValue = (() => {
      const now = new Date()
      if (mode === 'date') return toCalendarDate(now)
      if (mode === 'time') {
        const next = new Date(safeValue ?? now)
        next.setHours(now.getHours(), now.getMinutes(), 0, 0)
        return snapToMinuteStep(next)
      }
      return snapToMinuteStep(now)
    })()
    const nowAllowed = isValueAllowed(nowValue)

    const hours = useMemo(
      () => Array.from({ length: 12 }, (_, i) => pad2(i + 1)),
      [],
    )
    const minutes = useMemo(() => {
      const list: string[] = []
      for (let i = 0; i < 60; i += minuteStep) {
        list.push(pad2(i))
      }
      return list
    }, [minuteStep])

    const commitChange = (next: Date | null) => {
      if (value === undefined) {
        setInternalValue(next)
      }
      onChange(next)
    }

    const updateTime = (
      nextHour: string,
      nextMinute: string,
      nextPeriod: 'AM' | 'PM',
    ) => {
      if (!isClockAllowed(nextHour, nextMinute, nextPeriod)) return
      const baseDate = safeValue ?? new Date()
      commitChange(
        buildDateWithTime(baseDate, nextHour, nextMinute, nextPeriod),
      )
    }

    return (
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (disabled) return
          setOpen(next)
        }}
        modal
      >
        <PopoverTrigger asChild>
          <div className={cn('flex flex-col gap-2 w-full', labelClassName)}>
            {label && (
              <Label className="text-body-medium text-monochrome-1">
                {label}
              </Label>
            )}
            <Button
              ref={ref}
              type="button"
              size="sm"
              variant="tertiary"
              className={cn(
                'text-button-m text-monochrome-1 date-time-trigger !bg-monochrome-3-60 border border-monochrome-120 w-full justify-between h-[44px] gap-[10px] rounded-[16px] py-[10px] pl-[15px] pr-[12px]',
                hasError && 'border border-accent-3',
                open && 'border-accent-1',
                className,
              )}
              disabled={disabled}
            >
              <span className={cn(!safeValue && 'text-white/60')}>
                {safeValue
                  ? format(
                      safeValue,
                      mode === 'date'
                        ? 'MMM dd, yyyy'
                        : mode === 'time'
                        ? 'hh:mm a'
                        : 'MMM dd, yyyy • hh:mm a',
                    )
                  : placeholder}
              </span>
              <span className="flex shrink-0 items-center justify-center px-[8px] py-[4px]">
                <Icon
                  icon="arrow-up-down"
                  width="8"
                  height="16"
                  viewBox="0 0 8 16"
                />
              </span>
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)/2] bg-monochrome-3-60-solid rounded-3xl p-2.5 border-monochrome-120"
          align={mode === 'time' ? 'start' : align}
        >
          <div
            className={cn(
              mode === 'date'
                ? 'grid-cols-1'
                : mode === 'time'
                ? 'grid-cols-1'
                : 'grid grid-cols-[340px_130px]',
              'gap-1',
            )}
          >
            {mode !== 'time' && (
              <Calendar
                mode="single"
                selected={safeValue ?? undefined}
                disabled={calendarDisabled}
                className="bg-monochrome-3-60 rounded-3xl border border-monochrome-120 w-[340px]"
                onSelect={(date) => {
                  if (!date) {
                    commitChange(null)
                    return
                  }
                  if (mode === 'date') {
                    commitChange(toCalendarDate(date))
                  } else {
                    let next = buildDateWithTime(
                      date,
                      timeParts.hour,
                      timeParts.minute,
                      timeParts.period as 'AM' | 'PM',
                    )
                    if (
                      minDate &&
                      isValid(minDate) &&
                      isSameDay(next, minDate) &&
                      next < minDate
                    ) {
                      next = new Date(minDate)
                    }
                    if (
                      maxDate &&
                      isValid(maxDate) &&
                      isSameDay(next, maxDate) &&
                      next > maxDate
                    ) {
                      next = new Date(maxDate)
                    }
                    commitChange(next)
                  }
                }}
              />
            )}
            {(mode === 'datetime' || mode === 'time') && (
              <div className="bg-monochrome-3-60 rounded-3xl border border-monochrome-120 p-3 w-[130px] h-full flex flex-col">
                <div className="flex justify-between items-center gap-1">
                  <span className="text-body-medium text-white/70 flex-1">
                    Time
                  </span>
                  <Select
                    value={timeParts.period}
                    onValueChange={(value) =>
                      updateTime(
                        timeParts.hour,
                        timeParts.minute,
                        value as 'AM' | 'PM',
                      )
                    }
                  >
                    <SelectTrigger className="flex-1 h-8 px-2 bg-monochrome-3-60 rounded-full border border-monochrome-120 text-monochrome-1">
                      <SelectValue placeholder="Select Period" />
                    </SelectTrigger>
                    <SelectContent>
                      {(['AM', 'PM'] as const).map((period) => (
                        <SelectItem
                          key={period}
                          value={period}
                          disabled={
                            !hours.some((hour) =>
                              minutes.some((minute) =>
                                isClockAllowed(hour, minute, period),
                              ),
                            )
                          }
                        >
                          {period}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-1.5 w-full mt-3 max-h-[240px]">
                  <div className="overflow-y-auto flex flex-col w-full">
                    {hours.map((hour) => {
                      const hourAllowed = minutes.some((minute) =>
                        isClockAllowed(
                          hour,
                          minute,
                          timeParts.period as 'AM' | 'PM',
                        ),
                      )
                      return (
                        <button
                          key={hour}
                          type="button"
                          disabled={!hourAllowed}
                          className={cn(
                            'text-body-medium text-monochrome-1 px-1.5 py-1.5 w-full rounded-lg',
                            timeParts.hour === hour && 'bg-accent-1 text-white',
                            !hourAllowed && 'opacity-30 pointer-events-none',
                          )}
                          onClick={() =>
                            updateTime(
                              hour,
                              timeParts.minute,
                              timeParts.period as 'AM' | 'PM',
                            )
                          }
                        >
                          {hour}
                        </button>
                      )
                    })}
                  </div>
                  <div className="overflow-y-auto w-full">
                    {minutes.map((minute) => {
                      const minuteAllowed = isClockAllowed(
                        timeParts.hour,
                        minute,
                        timeParts.period as 'AM' | 'PM',
                      )
                      return (
                        <button
                          key={minute}
                          type="button"
                          disabled={!minuteAllowed}
                          className={cn(
                            'text-body-medium text-monochrome-1 px-1.5 py-1.5 w-full rounded-lg',
                            timeParts.minute === minute &&
                              'bg-accent-1 text-white',
                            !minuteAllowed && 'opacity-30 pointer-events-none',
                          )}
                          onClick={() =>
                            updateTime(
                              timeParts.hour,
                              minute,
                              timeParts.period as 'AM' | 'PM',
                            )
                          }
                        >
                          {minute}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            className={cn(
              mode === 'time' ? 'grid-cols-2' : 'grid-cols-[340px_130px]',
              'grid gap-2 w-full mt-2',
            )}
          >
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="w-full"
              disabled={!nowAllowed}
              onClick={() => {
                if (!nowAllowed) return
                commitChange(nowValue)
              }}
            >
              {mode === 'time' ? 'Now' : 'Today'}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="w-full"
              onClick={() => setOpen(false)}
            >
              OK
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    )
  },
)

DateTimePicker.displayName = 'DateTimePicker'

export default DateTimePicker
